import {useState} from 'react'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Hint} from '@/components/ui/hint'
import {HallazgoFotoUpload} from '@/components/hallazgo-foto-upload'
import {MyEntrevistaVersionRow} from '@/components/entrevista-version-row'
import {
  useDeshacerEntrevistaTurno,
  useMyEntrevista,
  useMyEntrevistaHistorial,
  usePostEntrevistaTextoLibre,
  usePostEntrevistaTurno,
  type EntrevistaControl,
  type Hallazgo,
} from '@/hooks/sgsst/riesgos/use-entrevista'
import {useMyOnboarding} from '@/hooks/empresa/identidad/use-onboarding'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

// Sin opciones y con la entrevista abierta solo puede ser la pregunta de cierre: el otro caso
// con `sugerencias` vacías es la entrevista completa.
function isCierreTextoLibre(entrevista: {completed_at: string | null; sugerencias: string[]}): boolean {
  return entrevista.completed_at === null && entrevista.sugerencias.length === 0
}

// `sugerencias` y `opciones` son paralelas por posición: el botón muestra el texto y envía el
// valor estable que exige el backend.
function OptionButtons({
  sugerencias,
  opciones,
  onPick,
  disabled,
}: {
  sugerencias: string[]
  opciones: string[]
  onPick: (opcion: string) => void
  disabled: boolean
}) {
  const {t} = useTranslation()
  if (sugerencias.length === 0) return null
  return (
    <div
      role="group"
      aria-label={t('entrevista.sugerenciasLabel')}
      data-testid="entrevista-sugerencias"
      className="grid gap-2.5">
      {sugerencias.map((texto, i) => (
        <Button
          key={opciones[i] ?? texto}
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => onPick(opciones[i] ?? texto)}
          data-testid="entrevista-sugerencia-chip"
          className="h-auto min-h-11 w-full justify-start rounded-xl px-4 py-3 text-left text-sm whitespace-normal">
          {texto}
        </Button>
      ))}
    </div>
  )
}

/** Deshace la última respuesta y todo lo que creó, para responder de nuevo la misma pregunta. */
function BackButton({onGoBack, disabled}: {onGoBack: () => void; disabled: boolean}) {
  const {t} = useTranslation()
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={onGoBack}
      data-testid="entrevista-back"
      className="text-muted-foreground">
      ← {t('entrevista.back.label')}
    </Button>
  )
}

/** Turno único de texto libre: enviarlo cierra la entrevista. */
function TextoLibreForm({onSubmit, disabled}: {onSubmit: (texto: string) => void; disabled: boolean}) {
  const {t} = useTranslation()
  const [texto, setTexto] = useState('')

  function submit() {
    const trimmed = texto.trim()
    if (!trimmed || disabled) return
    onSubmit(trimmed)
  }

  return (
    <div className="space-y-2.5" data-testid="entrevista-cierre-texto-libre">
      <Textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        maxLength={2000}
        minRows={4}
        disabled={disabled}
        placeholder={t('entrevista.cierre.placeholder')}
        data-testid="entrevista-cierre-texto-input"
      />
      <Button
        type="button"
        disabled={disabled || !texto.trim()}
        onClick={submit}
        data-testid="entrevista-cierre-texto-submit">
        {t('entrevista.cierre.submit')}
      </Button>
    </div>
  )
}

/** Siempre se puede saltar: la foto pendiente reaparece en la tarjeta «Fotos de sus riesgos» del inicio. */
function FotoInterstitial({
  businessId,
  hallazgo,
  onDone,
  onGoBack,
  goingBack,
}: {
  businessId: string
  hallazgo: Hallazgo
  onDone: () => void
  onGoBack: () => void
  goingBack: boolean
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-4 text-center" data-testid="entrevista-foto-interstitial">
      <p className="text-2xl" aria-hidden="true">
        📷
      </p>
      <div className="space-y-1">
        <p className="font-heading text-lg text-foreground">
          {t('entrevista.foto.interstitialHint', {hint: hallazgo.foto_hint})}
        </p>
        <p className="text-sm text-muted-foreground">{t('entrevista.foto.interstitialWhy')}</p>
      </div>
      <div className="mx-auto flex max-w-xs flex-col items-stretch gap-2">
        <HallazgoFotoUpload businessId={businessId} hallazgo={hallazgo} onUploaded={onDone} />
        <Button type="button" variant="ghost" size="sm" onClick={onDone} data-testid="entrevista-foto-skip">
          {t('entrevista.foto.interstitialSkip')}
        </Button>
      </div>
      {/* Deshace la respuesta que produjo este hallazgo. */}
      <div className="border-t border-border pt-2">
        <BackButton onGoBack={onGoBack} disabled={goingBack} />
      </div>
    </div>
  )
}

/** El control solo existe tras la aprobación, así que aún no admite foto: es una nota, no un paso.
 * Va encima de la siguiente pregunta porque reconoce la respuesta anterior. */
function ControlNudge({control}: {control: EntrevistaControl}) {
  const {t} = useTranslation()
  if (!control.evidencia_hint) return null
  return (
    <p className="border-b border-border pb-3 text-xs text-muted-foreground" data-testid="entrevista-control-nudge">
      {t('entrevista.control.nudge', {hint: control.evidencia_hint})}
    </p>
  )
}

function MisEvaluacionesSection({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data: historial} = useMyEntrevistaHistorial(businessId, page)
  if (!historial || historial.total <= 1) return null
  return (
    <div className="mt-6 border-t border-border pt-4 text-left" data-testid="mis-evaluaciones">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('employees.detail.misEvaluacionesTitle')}
      </p>
      {historial.items.map((version) => (
        <MyEntrevistaVersionRow key={version.id} businessId={businessId} version={version} />
      ))}
      <PaginationBar
        page={historial.page}
        pageSize={historial.page_size}
        total={historial.total}
        onPageChange={setPage}
      />
    </div>
  )
}

/** Sin perfil sellado no hay «Volver al inicio»: el asistente sigue solo al perfil y ese botón
 * sería la salida para dejarlo después (KAI-219). */
function CompleteState({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: onboarding} = useMyOnboarding(businessId)
  const perfilPendiente = onboarding === undefined || !onboarding.perfil_sellado_vigente
  return (
    <div className="space-y-3 text-center" data-testid="entrevista-complete-banner">
      <p className="font-heading text-lg text-foreground">{t('entrevista.completeTitle')}</p>
      <p className="text-sm text-muted-foreground">{t('entrevista.completeBanner')}</p>
      {!perfilPendiente && (
        <Button asChild size="sm" variant="outline">
          <Link to={`/businesses/${businessId}`}>{t('entrevista.backToHome')}</Link>
        </Button>
      )}
      <MisEvaluacionesSection businessId={businessId} />
    </div>
  )
}

export function EntrevistaRiesgosStep({businessId}: {businessId: string}) {
  const {t} = useTranslation()
  const {data: entrevista, isLoading, isError} = useMyEntrevista(businessId)
  const postTurno = usePostEntrevistaTurno(businessId)
  const postTextoLibre = usePostEntrevistaTextoLibre(businessId)
  const deshacer = useDeshacerEntrevistaTurno(businessId)
  const [error, setError] = useState<string | null>(null)
  // Local a la sesión: al recargar se vuelve a ofrecer la foto pendiente.
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set())

  if (isLoading) return <Skeleton className="h-72 w-full" />
  if (isError || !entrevista)
    return (
      <div className="space-y-2" data-testid="mi-entrevista">
        <p className="text-sm text-muted-foreground">{t('entrevista.noRecord')}</p>
      </div>
    )

  const completa = entrevista.completed_at !== null
  const cubiertas = entrevista.familias_cubiertas.length
  const isPending = postTurno.isPending || postTextoLibre.isPending || deshacer.isPending
  // El primer turno es la pregunta inicial; desde el segundo hay una respuesta que deshacer.
  const canGoBack = !completa && entrevista.turnos.length > 1
  const latestHallazgo = (entrevista.hallazgos ?? []).at(-1)
  const pendingFotoHallazgo =
    latestHallazgo && latestHallazgo.foto_hint && !latestHallazgo.has_foto && !acknowledged.has(latestHallazgo.id)
      ? latestHallazgo
      : null
  // No bloquea: se reconoce al enviar la siguiente respuesta.
  const latestControl = (entrevista.controles ?? []).at(-1)
  const freshControl = latestControl && !acknowledged.has(latestControl.id) ? latestControl : null
  const cierreTextoLibre = isCierreTextoLibre(entrevista)

  function acknowledge(id: string) {
    setAcknowledged((prev) => new Set(prev).add(id))
  }

  function submitOpcion(opcion: string) {
    if (completa || isPending) return
    setError(null)
    if (freshControl) acknowledge(freshControl.id)
    postTurno.mutate({opcion}, {onError: (err) => setError(translateApiError(err))})
  }

  function submitTextoLibre(texto: string) {
    if (completa || isPending) return
    setError(null)
    if (freshControl) acknowledge(freshControl.id)
    postTextoLibre.mutate({texto}, {onError: (err) => setError(translateApiError(err))})
  }

  function goBack() {
    if (completa || isPending || !canGoBack) return
    setError(null)
    deshacer.mutate(undefined, {onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="space-y-4" data-testid="mi-entrevista">
      {!completa && entrevista.familias_cubiertas.length === 0 && (
        <h2 className="font-heading text-lg text-foreground">{t('entrevista.title')}</h2>
      )}

      <div className="flex items-center justify-between">
        <span
          data-testid="entrevista-progress"
          className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {completa
            ? t('entrevista.progressComplete')
            : t('entrevista.progress', {covered: cubiertas, total: entrevista.familias_totales})}
        </span>
        {completa && (
          <Tag tone="green" data-testid="entrevista-complete-tag">
            {t('entrevista.completeTag')}
          </Tag>
        )}
      </div>

      <div className="flex gap-1" aria-hidden="true">
        {Array.from({length: entrevista.familias_totales}, (_, i) => i + 1).map((n) => (
          <span
            key={n}
            className={cn('h-1.5 flex-1 rounded-full', completa || n <= cubiertas ? 'bg-primary' : 'bg-secondary')}
          />
        ))}
      </div>

      {completa ? (
        <CompleteState businessId={businessId} />
      ) : pendingFotoHallazgo ? (
        // Reemplaza la pregunta: `turnos.at(-1)` ya trae la siguiente, que no debe verse antes de
        // reconocer el hallazgo.
        <FotoInterstitial
          businessId={businessId}
          hallazgo={pendingFotoHallazgo}
          onDone={() => acknowledge(pendingFotoHallazgo.id)}
          onGoBack={goBack}
          goingBack={isPending}
        />
      ) : (
        <div className="space-y-4">
          <div className="notice-amber rounded-lg px-3.5 py-2.5 text-xs" data-testid="entrevista-ack-banner">
            {t('entrevista.ackBanner')}
          </div>

          {freshControl && <ControlNudge control={freshControl} />}

          {entrevista.turnos.length > 0 && (
            <p data-testid="entrevista-current-question" className="text-base font-medium text-foreground">
              {entrevista.turnos.at(-1)?.texto}
            </p>
          )}

          {cierreTextoLibre ? (
            <TextoLibreForm onSubmit={submitTextoLibre} disabled={isPending} />
          ) : (
            <>
              {entrevista.familias_cubiertas.length === 0 && (
                <Hint testId="entrevista-hint">{t('entrevista.subtitle')}</Hint>
              )}
              <OptionButtons
                sugerencias={entrevista.sugerencias}
                opciones={entrevista.opciones ?? entrevista.sugerencias}
                onPick={submitOpcion}
                disabled={isPending}
              />
            </>
          )}

          {error && (
            <p className="text-sm text-destructive" data-testid="entrevista-error">
              {error}
            </p>
          )}

          {canGoBack && (
            <div className="pt-1">
              <BackButton onGoBack={goBack} disabled={isPending} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
